import React, { useState, useEffect, useRef } from 'react';
import './Toast.css';

export default function Toast({
  isOpen,
  message,
  icon = null,
  duration = 3200,
  toastKey = null,
  onClose,
  actionText = null,
  onAction = null,
}) {
  const [isRendered, setIsRendered] = useState(Boolean(isOpen));
  const [isExiting, setIsExiting] = useState(false);
  
  const onCloseRef = useRef(onClose);
  const onActionRef = useRef(onAction);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onActionRef.current = onAction;
  }, [onAction]);

  useEffect(() => {
    let hideTimer = null;
    let unmountTimer = null;

    if (isOpen) {
      setIsRendered(true);
      setIsExiting(false);

      hideTimer = setTimeout(() => {
        setIsExiting(true);
        unmountTimer = setTimeout(() => {
          setIsRendered(false);
          setIsExiting(false);
          if (onCloseRef.current) {
            onCloseRef.current();
          }
        }, 220);
      }, duration);
    } else {
      if (isRendered && !isExiting) {
        setIsExiting(true);
        unmountTimer = setTimeout(() => {
          setIsRendered(false);
          setIsExiting(false);
        }, 220);
      }
    }

    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      if (unmountTimer) clearTimeout(unmountTimer);
    };
    // Crucial: Only depend on primitive props that define the toast lifecycle.
    // Do NOT include functions (like onClose) or internal states (isRendered, isExiting)
    // which would cause continuous resets on parent re-renders during video playback.
  }, [isOpen, message, duration, toastKey]);

  if (!isRendered) return null;

  const renderIcon = () => {
    if (!icon) return null;
    if (React.isValidElement(icon)) {
      return icon;
    }
    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent size={20} />;
    }
    return null;
  };

  return (
    <div className={`yt-toast-container ${isExiting ? 'exiting' : ''}`}>
      {icon && (
        <div className="yt-toast-icon">
          {renderIcon()}
        </div>
      )}
      <span className="yt-toast-message">{message}</span>
      {actionText && (
        <button
          className="yt-toast-action-btn"
          type="button"
          onClick={() => {
            if (onActionRef.current) onActionRef.current();
            if (onCloseRef.current) onCloseRef.current();
          }}
        >
          {actionText}
        </button>
      )}
    </div>
  );
}
